import React from 'react';
import {FlexRow, Link, Pill, PillVariant, Text, Tooltip} from '@forecast-it/design-system';
import {Column} from '../Elements.styled';
import {
	arePhaseTasksComplete,
	derivePhaseSignoffStatus,
	PHASE_SIGNOFF_STATUS_LABEL_ID,
	PhaseSignoffStatus,
	resolveSignoffApproverLabel,
} from '../../../../../util/phaseSignoffStatus';
import {showSendSignoffModal} from '../../../../modals/send-signoff/SendSignoffModal';
import {confirmCancelPhaseSignoffRequest} from '../../../../modals/send-signoff/cancelPhaseSignoffRequest';
import {showReassignSignoffApproverModal} from '../../../../modals/reassign-signoff-approver/ReassignSignoffApproverModal';

// Visual weight for each state: SUCCESS for the terminal signed-off row,
// INFORMATION for states where the firm or its client has an action to take,
// NEUTRAL for the passive states. Pill supplies its own accessible contrast in
// both light and dark themes so the label stays legible.
const SIGNOFF_STATUS_PILL_VARIANT = {
	[PhaseSignoffStatus.NOT_CONFIGURED]: PillVariant.NEUTRAL,
	[PhaseSignoffStatus.REQUIRED_BUT_INCOMPLETE]: PillVariant.NEUTRAL,
	[PhaseSignoffStatus.READY_TO_SEND]: PillVariant.INFORMATION,
	[PhaseSignoffStatus.AWAITING_CLIENT]: PillVariant.INFORMATION,
	[PhaseSignoffStatus.CHANGES_REQUESTED]: PillVariant.INFORMATION,
	[PhaseSignoffStatus.SIGNED_OFF]: PillVariant.SUCCESS,
};

/**
 * The sign-off lock moment in the viewer's locale. Returns null for a missing or
 * unparseable timestamp, so the badge degrades to the plain state label rather
 * than printing "Invalid date".
 */
const formatLockedDate = (isoString, intl) => {
	if (!isoString) return null;
	const parsed = new Date(isoString);
	if (Number.isNaN(parsed.getTime())) return null;
	return intl.formatDate(parsed, {year: 'numeric', month: 'short', day: 'numeric'});
};

/**
 * A `YYYY-MM-DD` calendar date — the shape the sign-off deadline is stored and sent in.
 * Pinned to UTC on purpose: the string names a day, not an instant, so rendering it in
 * the viewer's zone would print the day before it for anyone west of UTC. Null for a
 * missing or unparseable value, so the cell shows the bare state instead.
 */
const formatCalendarDate = (dateString, intl) => {
	if (!dateString) return null;
	const parsed = new Date(`${dateString}T00:00:00Z`);
	if (Number.isNaN(parsed.getTime())) return null;
	return intl.formatDate(parsed, {year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'});
};

/**
 * The Sign-off Status cell of a phase header row: a state pill for one of the six
 * client sign-off states, plus whichever detail that state has to offer — when a locked
 * phase was signed off and by whom, the deadline an outstanding request is due by, or
 * the note a client attached when they asked for changes.
 *
 * Three of the six states are also action surfaces, and only those three. *Ready to send*
 * makes the pill itself open the Send sign-off modal; *Awaiting client* offers a reassign
 * that hands the live request to a different client user, and a cancel that withdraws it;
 * *Changes requested* offers a Resend that opens the Send sign-off modal for a second
 * round, prefilling the live request's approver rather than the phase's configured one.
 * The other three are labels — reassignment in particular is post-send and pre-decision,
 * so neither a request that has not gone out nor one the client has answered offers it.
 *
 * Every detail degrades independently, and none is asserted when it cannot be read: an
 * approver who has lost workspace eligibility drops out of `portalEligibleApprovers`, a
 * request may carry no deadline, and a client may have asked for changes without saying
 * what. The cell composes whatever it actually has and falls back to the bare pill.
 *
 * `phase` may be null (a header row with no phase), in which case the cell reports
 * Not configured rather than rendering blank. A phase with no resolvable `project` drops
 * both modal-opening actions and keeps the rest: the send modal needs the parent to anchor
 * the request to a client org and the reassign modal needs it to load the eligible
 * approvers, so offering either without it is offering something that must fail. Cancel
 * needs only the phase, so it survives.
 */
export const PhaseSignoffStatusColumn = ({phase, project, rolledUpValues, portalEligibleApprovers, width, align, intl}) => {
	const portalConfig = phase && phase.portalConfig ? phase.portalConfig : null;
	// Hoisted so both the derivation and the Changes-requested resend gate read the same
	// value — a task reopened after the client asked for changes must disable the
	// resend, and re-deriving completeness twice invites the two branches to drift.
	const tasksComplete = arePhaseTasksComplete(rolledUpValues);
	const status = derivePhaseSignoffStatus({
		requiresClientSignoff: portalConfig ? portalConfig.requiresClientSignoff : false,
		signoffLocked: portalConfig ? portalConfig.signoffLocked : false,
		tasksComplete,
		requestState: portalConfig ? portalConfig.signoffRequestState : null,
	});
	const label = intl.formatMessage({id: PHASE_SIGNOFF_STATUS_LABEL_ID[status]});

	const isSignedOff = status === PhaseSignoffStatus.SIGNED_OFF;
	const lockedDate = isSignedOff && portalConfig ? formatLockedDate(portalConfig.signoffLockedAt, intl) : null;
	const approverName = isSignedOff
		? resolveSignoffApproverLabel(portalConfig ? portalConfig.signoffApproverPortalUserId : null, portalEligibleApprovers)
		: null;

	// The approver on the LIVE request, which is not the same value as the phase's
	// configured approver: a reassignment moves this one and leaves the configuration
	// alone, so naming the configured one would keep showing the person who was replaced.
	const requestApproverPortalUserId = portalConfig ? portalConfig.signoffRequestApproverPortalUserId : null;
	const requestApproverName = resolveSignoffApproverLabel(requestApproverPortalUserId, portalEligibleApprovers);

	// One detail slot, shared by the three states that have something to add beside the
	// pill — which is also what keeps the cell's width behaviour identical across them.
	let detail = null;
	if (lockedDate && approverName) {
		detail = intl.formatMessage(
			{id: 'client_portal.signoff_state.signed_off_detail_date_name'},
			{date: lockedDate, name: approverName}
		);
	} else if (lockedDate) {
		detail = lockedDate;
	} else if (approverName) {
		detail = intl.formatMessage({id: 'client_portal.signoff_state.signed_off_detail_name'}, {name: approverName});
	} else if (status === PhaseSignoffStatus.AWAITING_CLIENT) {
		// Who it is with, and by when — both optional, so all four combinations render.
		// The name is what makes a reassignment visible in the row: after one succeeds the
		// mutation merges the new approver and this detail re-reads it.
		const deadline = formatCalendarDate(portalConfig ? portalConfig.signoffRequestDeadline : null, intl);
		if (requestApproverName && deadline) {
			detail = intl.formatMessage(
				{id: 'client_portal.signoff_state.awaiting_client_detail_name_deadline'},
				{name: requestApproverName, date: deadline}
			);
		} else if (requestApproverName) {
			detail = requestApproverName;
		} else {
			detail = deadline
				? intl.formatMessage({id: 'client_portal.signoff_state.awaiting_client_deadline'}, {date: deadline})
				: null;
		}
	} else if (status === PhaseSignoffStatus.CHANGES_REQUESTED) {
		detail = (portalConfig && portalConfig.signoffChangesRequestedNote) || null;
	}

	const pill = <Pill variant={SIGNOFF_STATUS_PILL_VARIANT[status]}>{label}</Pill>;
	const phaseName = (phase && phase.name) || '';
	const canSend = status === PhaseSignoffStatus.READY_TO_SEND && phase && phase.id && project && project.id;
	// Read once so the Ready-to-send and Resend entry points cannot hand the modal
	// different numbers; it splits these into firm- and client-owned groups.
	const taskCounts = {
		taskCount: rolledUpValues ? rolledUpValues.taskCount : null,
		doneCount: rolledUpValues ? rolledUpValues.doneCount : null,
		clientOwnedTaskCount: rolledUpValues ? rolledUpValues.clientOwnedTaskCount : null,
		clientOwnedDoneCount: rolledUpValues ? rolledUpValues.clientOwnedDoneCount : null,
	};

	/**
	 * The phase header row collapses and expands on a click anywhere in it, so an action
	 * inside one of its cells has to stop the event or it does its own job AND folds the
	 * phase away underneath the dialog it just opened. Same guard the editable phase-name
	 * and custom-field cells in this row already use.
	 */
	const action = handler => event => {
		event.stopPropagation();
		event.preventDefault();
		handler();
	};

	return (
		<Column width={width} align={align}>
			<FlexRow data-cy="phase-signoff-status">
				{canSend ? (
					<Link
						onClick={action(() =>
							showSendSignoffModal({
								phaseId: phase.id,
								projectId: project.id,
								phaseName,
								...taskCounts,
								approverPortalUserId: portalConfig ? portalConfig.signoffApproverPortalUserId : null,
							})
						)}
						// The pill reads "Ready to send", which does not say what pressing it does,
						// and every such link on the page would otherwise share that one name.
						aria-label={intl.formatMessage(
							{id: 'client_portal.signoff_state.ready_to_send_action_aria'},
							{name: phaseName}
						)}
						data-cy="phase-signoff-send"
					>
						{pill}
					</Link>
				) : (
					pill
				)}
				{detail && (
					<Text color={'auxiliary'} ellipsis tooltipOnEllipsis data-cy="phase-signoff-detail">
						{detail}
					</Text>
				)}
				{status === PhaseSignoffStatus.AWAITING_CLIENT && phase && phase.id && project && project.id && (
					<Link
						onClick={action(() =>
							showReassignSignoffApproverModal({
								phaseId: phase.id,
								projectId: project.id,
								phaseName,
								currentApproverPortalUserId: requestApproverPortalUserId,
							})
						)}
						aria-label={intl.formatMessage(
							{id: 'client_portal.signoff_state.reassign_request_aria'},
							{name: phaseName}
						)}
						data-cy="phase-signoff-reassign"
					>
						{intl.formatMessage({id: 'client_portal.signoff_state.reassign_request'})}
					</Link>
				)}
				{status === PhaseSignoffStatus.AWAITING_CLIENT && phase && phase.id && (
					<Link
						onClick={action(() => confirmCancelPhaseSignoffRequest({phaseId: phase.id, phaseName, intl}))}
						aria-label={intl.formatMessage(
							{id: 'client_portal.signoff_state.cancel_request_aria'},
							{name: phaseName}
						)}
						data-cy="phase-signoff-cancel"
					>
						{intl.formatMessage({id: 'client_portal.signoff_state.cancel_request'})}
					</Link>
				)}
				{status === PhaseSignoffStatus.CHANGES_REQUESTED &&
					phase &&
					phase.id &&
					project &&
					project.id &&
					(tasksComplete ? (
						<Link
							onClick={action(() =>
								showSendSignoffModal({
									phaseId: phase.id,
									projectId: project.id,
									phaseName,
									...taskCounts,
									// The LIVE request's approver, not the phase's configured one:
									// a reassignment moves the request and leaves the config alone,
									// so the configured value would prefill the person who was replaced.
									approverPortalUserId:
										requestApproverPortalUserId ||
										(portalConfig ? portalConfig.signoffApproverPortalUserId : null),
								})
							)}
							aria-label={intl.formatMessage(
								{id: 'client_portal.signoff_state.resend_request_aria'},
								{name: phaseName}
							)}
							data-cy="phase-signoff-resend"
						>
							{intl.formatMessage({id: 'client_portal.signoff_state.resend_request'})}
						</Link>
					) : (
						// A <Link disabled onClick={…}> would still fire — Link destructures the
						// disabled prop out before spreading, so its own [disabled] rule never
						// matches and the click handler runs. A disabled Text under a Tooltip
						// carries the intent without a live click surface.
						<Tooltip
							content={intl.formatMessage({id: 'client_portal.signoff_state.resend_blocked_tooltip'})}
							interactable={false}
						>
							<Text color={'disabled'} data-cy="phase-signoff-resend-disabled">
								{intl.formatMessage({id: 'client_portal.signoff_state.resend_request'})}
							</Text>
						</Tooltip>
					))}
			</FlexRow>
		</Column>
	);
};
